import { SlotOP } from '@cemex-core/cmx-api';
import { Store } from '@ngrx/store';
import { ScheduleSlot, TimeSlots } from '@ordersnproducts/modules/shared/models/dco/cwc-time-slot.model';
import { NO_AVAILABILITY_ERROR_CODE } from '@ordersnproducts/modules/shared/models/dco/order-status-modify-snapshot.model';
import { getDcoEntitiesAsArray, getOutOfServiceState } from '@ordersnproducts/modules/shared/reducers';
import { OutOfServiceHours } from '@ordersnproducts/modules/shared/reducers/dco.reducer';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { take } from 'rxjs/operators';
import { RmsNextWorkingDayResponse } from '@cemex-core/cmx-api/models/next-working-day/next-working-day-res';
import * as moment from 'moment';

/**
 * Checks if the jobsite for a given slot is an extended range jobsite.
 * @param slot The response object from DCO.
 * @param plantSlot plant code of a slot.
 * @returns Returns true if the jobsite is an extended range, otherwise returns false.
 */
export function checkIsExtendedRangeJobsite(slot: SlotOP, plantSlot: string) {
  if (!plantSlot) {
    return false;
  }
  const plantCatalogList = _safe(() => slot.plantCatalog, []);
  const plantItem = plantCatalogList.find((plant) => plant.PlantCode === plantSlot);
  return !!_safe(() => plantItem.additionalTime, '');
}

/**
 * Converts the schedule slots(data) into timeSlots list that represent cwc-time-slot's timeSlots input.
 * This function also checks if the slot is an extended range jobsite and adds the colorTag property to the timeSlot object.
 * @param schedule list of time slots data
 * @param slotOP object that contains the plant catalog
 * @param colorTagMessage Text to show in the color tag in case of extended range jobsite
 * @param isEnableOutRangeTag Flag to enable or disable the color tag for extended range jobsite
 * @returns a list of TimeSlots objects
 */
export function convertCwcTimeSlots(
  schedule: ScheduleSlot[],
  slotOP: SlotOP,
  colorTagMessage: string,
  isEnableOutRangeTag: boolean,
  recommendedTimeSlotSelected?: any,
): TimeSlots[] {
  return schedule.map((slotItem) => {
    const timeSlot: TimeSlots = {
      time: slotItem.time,
      warning: slotItem.warning,
    };
    if (recommendedTimeSlotSelected && slotItem.time === recommendedTimeSlotSelected.timeDisplay) {
      timeSlot.selected = true;
    }
    if (isEnableOutRangeTag && checkIsExtendedRangeJobsite(slotOP, slotItem.plant)) {
      timeSlot.colorTag = 'warning';
      timeSlot.colorTagMessage = colorTagMessage;
      timeSlot.colorTagShowIcon = true;
      timeSlot.colorTagIcon = 'warning';
    }
    return timeSlot;
  });
}

/**
 * Validates if the selected slot is an extended range slot.
 * When the selected slot has 'warning' colorTag indicates that the slot is an extended range slot.
 *
 * @param normalTime time string of selected slot
 * @param timeSlotList list of TimeSlots
 * @returns return true if the selected slot is an extended range slot, otherwise return false.
 */
export function validateSlotWithExtendedRange(normalTime: string, timeSlotList: TimeSlots[]): boolean {
  return (
    timeSlotList.find((slot) => slot.time === normalTime && _safe(() => slot.colorTag, '') === 'warning') !== undefined
  );
}

/**
 * Enable/disable the next button when out of service hours is active.
 * when the out of service hours is disable or the dco feature is disable no make any action.
 * @param outOfService status of out of service hours
 * @param notCheckSlot flag to check if the slot is selected
 * @param callbackOutActive callback to enable the next button when out of service hours is active
 * @param callbackOutDisable callback to disable the next button when out of service hours is disable
 * @returns
 */
export function enableNextButtonByOutOfServiceHoursFn(
  outOfService: OutOfServiceHours,
  notCheckSlot: boolean,
  dcoFeatureEnabled: boolean,
  callbackOutActive: (enable: boolean) => void,
  callbackOutDisable: () => void,
): void {
  if (dcoFeatureEnabled && outOfService.isOutOfServiceHours) {
    if (outOfService.check && notCheckSlot) {
      return callbackOutActive(true);
    } else if (notCheckSlot) {
      return callbackOutActive(false);
    }
    callbackOutDisable();
  }
}

/**
 * Determines if has selected slot.
 */
export function isSelectedSlot(dateRecipes: any[]) {
  const _isCheckSlot = dateRecipes.some(
    (dateRecipe) =>
      !!dateRecipe.selectedDatetimeSlot ||
      !!dateRecipe.requestDatetimeIsAvailable ||
      (dateRecipe.error && dateRecipe.error.httpStatusReasonCode === NO_AVAILABILITY_ERROR_CODE),
  );
  return _isCheckSlot;
}

/**
 * Get the out of service hours state from the store.
 * @returns state of out of service hours
 */
export function getActiveOutOfServiceHoursSO(store: Store<any>): OutOfServiceHours {
  let state: OutOfServiceHours;
  store
    .select(getOutOfServiceState)
    .pipe(take(1))
    .subscribe((_state) => {
      state = _state;
    });
  return state;
}

/**
 * @returns true if the user has selected a slot or the request datetime is available, otherwise false.
 */
export function isCheckSlotSO(store: Store<any>): boolean {
  const dateRecipe$ = store.select(getDcoEntitiesAsArray);
  let _isCheckSlot = false;
  dateRecipe$.pipe(take(1)).subscribe((dateRecipes) => {
    _isCheckSlot = _safe(() => isSelectedSlot(dateRecipes), false);
  });
  return _isCheckSlot;
}

/**
 * Calculates the cut-off indicator based on the provided cut-off state.
 * The function determines whether the current time is before or after the cut-off time.
 * If the current hour is less than the cut-off hour, it returns 0. Otherwise, it returns 1.
 *
 * @param cutOffState - The response object containing the cut-off state information.
 * @returns Returns 0 if the current time is before the cut-off time, otherwise returns 1.
 *
 */
export function calculateCutOffIndicator(cutOffState: RmsNextWorkingDayResponse) {
  const timeZone = cutOffState.timeZone && cutOffState.timeZone['iANACode'];
  const time = cutOffState.timeCut.replace('.000Z', '');
  if (timeZone) {
    const todayInTimeZone = moment().tz(timeZone);
    const cutoffWithJobsiteTimeZone = moment.tz(time, 'HH:mm', timeZone);
    return todayInTimeZone.isBefore(cutoffWithJobsiteTimeZone) ? 0 : 1;
  } else {
    const today = moment();
    const jobsiteCutoffTime = moment(time, 'HH:mm');
    return today.isBefore(jobsiteCutoffTime) ? 0 : 1;
  }
}
